// The standings' side column (B-387): the race for the playoff seats, the
// points, and at Everything the all-play record. Facts only: records, games
// and points, never what to do about them.

import type { ReactNode } from 'react';
import type { StandingsRow, StandingsView } from '@fsp/domain';
import { Link } from 'react-router-dom';
import { useAtLeast } from '../../data/detail';
import { useLeagueBase } from '../../data/hooks';
import { TeamMark } from '../../kit';
import { Panel } from '../../kit/Panel';
import { Button, Meter } from '../../kit/ui';
import { Term } from '../../kit/Why';
import { GLOSSARY } from '../shared/glossary';

export type TableKind = 'h2h' | 'race' | 'allplay';

/** "2–1", or "2–1–1" with ties. */
function recordText(r: { wins: number; losses: number; ties: number }): string {
  return `${r.wins}–${r.losses}${r.ties ? `–${r.ties}` : ''}`;
}

/** A record's share of games won, a tie half a win, as a table prints it: ".667". */
function shareText(r: { wins: number; losses: number; ties: number }): string | null {
  const games = r.wins + r.losses + r.ties;
  if (games === 0) return null;
  return ((r.wins + r.ties / 2) / games).toFixed(3).replace(/^0/, '');
}

/** Games from the line, as the race says it: "+1", "1.5 back", a dash for level. */
export function fromLineText(games: number): string {
  if (games === 0) return '—';
  return games > 0 ? `+${games}` : `${-games} back`;
}

/**
 * CLINCHED OR OUT, in a word beside the name (B-397): the server's
 * `playoffStatus`, certain and never likely. Nothing while a team is still
 * in the race, and nothing where it is not measured. Words, not a number:
 * at every level.
 */
export function RaceMark({ status }: { status: StandingsRow['playoffStatus'] }) {
  if (status === 'clinched')
    return (
      <span className="fsp-st-mark" data-race="in" title="Clinched a playoff spot">
        Clinched
      </span>
    );
  if (status === 'eliminated')
    return (
      <span className="fsp-st-mark" data-race="out" title="Out of the playoff race">
        Out
      </span>
    );
  return null;
}

/** One team in a side list: its place, its mark and name, and what the list measures. */
function TeamLine({
  row,
  place,
  children,
}: {
  row: StandingsRow;
  place: number | null;
  children: ReactNode;
}) {
  const base = useLeagueBase();
  return (
    <Link
      to={`${base}/teams/${row.team.id}`}
      className="fsp-st-line-row"
      aria-current={row.team.isMine || undefined}
    >
      <span className="fsp-st-place fsp-num">{place ?? '—'}</span>
      <TeamMark team={row.team} size={20} />
      {children}
    </Link>
  );
}

/**
 * THE RACE FOR THE SEATS (B-387): the last teams in, the line, the first
 * teams out and mine wherever it is, each with its record and from Standard
 * how far it is from the line (`gamesFromLine`), the wild-card column a
 * baseball table carries. "Am I in" without the arithmetic (scenario H1).
 */
export function PlayoffRace({
  view,
  weeksLeft,
}: {
  view: StandingsView;
  weeksLeft: number | null;
}) {
  const standard = useAtLeast('standard');
  const base = useLeagueBase();
  const contenders = view.rows.filter((r) => r.gamesFromLine !== null);
  const inside = contenders.filter((r) => r.seed !== null).sort((a, b) => a.seed! - b.seed!);
  const outside = contenders.filter((r) => r.seed === null).sort((a, b) => a.rank! - b.rank!);
  // Under division winners first a leader is in by its division, and the
  // race is for the seats the leaders leave.
  const leaders =
    contenders.length > 0
      ? view.rows.filter((r) => r.seed !== null && r.gamesFromLine === null)
      : [];
  const shown = (list: StandingsRow[], near: StandingsRow[]) => {
    const keep = new Set(near.map((r) => r.team.id));
    return list.filter((r) => keep.has(r.team.id) || r.team.isMine);
  };
  const shownIn = shown(inside, inside.slice(-2));
  const shownOut = shown(outside, outside.slice(0, 3));
  /** The rows of one side, with a gap marked where the list skips a team. */
  const lines = (list: StandingsRow[], all: StandingsRow[]) =>
    list.map((r, i) => {
      const skipped = i > 0 && all.indexOf(r) - all.indexOf(list[i - 1]!) > 1;
      return (
        <div key={r.team.id}>
          {skipped ? <div className="fsp-st-skip" aria-hidden="true" /> : null}
          <TeamLine row={r} place={r.rank}>
            <span className="fsp-st-name">
              {r.team.name} <RaceMark status={r.playoffStatus} />
            </span>
            <span className="fsp-st-rec fsp-num">{recordText(r)}</span>
            {standard ? (
              <span className="fsp-st-from fsp-num" data-side={r.seed !== null ? 'in' : 'out'}>
                {fromLineText(r.gamesFromLine!)}
              </span>
            ) : null}
          </TeamLine>
        </div>
      );
    });
  return (
    <Panel
      title="Playoff race"
      meta={
        standard && contenders.length > 0 ? (
          <span className="fsp-muted fsp-sb-count">
            <Term tip={GLOSSARY.fromLine}>Games from the line</Term>
          </span>
        ) : null
      }
    >
      <div className="fsp-st-race">
        <p>
          {view.playoffTeamCount >= view.rows.length
            ? 'Every team makes the playoffs.'
            : `${view.playoffTeamCount} of ${view.rows.length} teams make the playoffs.`}
          {view.regularSeasonEnd !== null ? (
            <>
              {' '}
              The regular season ends with Week {view.regularSeasonEnd}
              {/* How many weeks are left is a number: from Standard. */}
              {standard && weeksLeft !== null && weeksLeft > 0
                ? `, ${weeksLeft} week${weeksLeft === 1 ? '' : 's'} to play`
                : ''}
              .
            </>
          ) : null}
        </p>
        {leaders.length > 0 ? (
          <p className="fsp-muted">
            {leaders.length === 1 ? 'The division leader is' : 'Each division’s leader is'} in; the
            race is for the seats left.
          </p>
        ) : null}
        {contenders.length > 0 ? (
          <div className="fsp-st-race-list" aria-label="The teams either side of the playoff line">
            {lines(shownIn, inside)}
            <div className="fsp-st-line" role="separator" aria-label="Playoff line">
              <span>Playoff line</span>
            </div>
            {lines(shownOut, outside)}
          </div>
        ) : view.playoffTeamCount < view.rows.length && view.rows.some((r) => r.rank === null) ? (
          <p className="fsp-muted">The race starts once a week is final.</p>
        ) : null}
        <span>
          <Button size="xs" variant="quiet" icon="podium" to={`${base}/schedule?tab=playoffs`}>
            Playoff picture
          </Button>
        </span>
      </div>
    </Panel>
  );
}

/**
 * EVERY TEAM'S POINTS in order, a bar each against the most (B-387): who
 * scores, whatever the schedule did with it. Numbers, so from Standard.
 */
export function PointsRace({ view }: { view: StandingsView }) {
  const rows = [...view.rows].sort((a, b) => b.pointsFor - a.pointsFor);
  const most = rows[0]?.pointsFor ?? 0;
  if (most <= 0) return null;
  return (
    <Panel
      title="Points"
      meta={
        <span className="fsp-muted fsp-sb-count">
          <Term tip={GLOSSARY.pointsFor}>Points for</Term>
        </span>
      }
    >
      <div className="fsp-st-list" aria-label="Every team, the most points first">
        {rows.map((r) => (
          <TeamLine
            key={r.team.id}
            row={r}
            // Level points share a place.
            place={1 + rows.filter((o) => o.pointsFor > r.pointsFor).length}
          >
            <span className="fsp-st-name-bar">
              <span className="fsp-st-name">{r.team.name}</span>
              <Meter value={r.pointsFor / most} />
            </span>
            <span className="fsp-st-rec fsp-num">{r.pointsFor.toFixed(1)}</span>
          </TeamLine>
        ))}
      </div>
    </Panel>
  );
}

/**
 * MAX PF (B-419): each team's best possible lineups summed beside the points
 * it scored, the most first, and the share it got of them — Sleeper's Max
 * PF, the big sites' potential points. The bar is that share. A team whose
 * weeks were not all written down has no Max PF, and is left out.
 */
export function MaxPoints({ view }: { view: StandingsView }) {
  const rows = view.rows
    .filter((r): r is StandingsRow & { maxPoints: number } => r.maxPoints !== null)
    .sort((a, b) => b.maxPoints - a.maxPoints);
  if (rows.length === 0 || rows[0]!.maxPoints <= 0) return null;
  return (
    <Panel
      title="Max PF"
      meta={
        <span className="fsp-muted fsp-sb-count">
          <Term tip={GLOSSARY.maxPoints}>What this is</Term>
        </span>
      }
    >
      <div className="fsp-st-list" aria-label="Every team, the most possible points first">
        {rows.map((r) => (
          <TeamLine
            key={r.team.id}
            row={r}
            place={1 + rows.filter((o) => o.maxPoints > r.maxPoints).length}
          >
            <span className="fsp-st-name-bar">
              <span className="fsp-st-name">{r.team.name}</span>
              <Meter value={r.maxPoints > 0 ? r.pointsFor / r.maxPoints : 0} />
            </span>
            <span className="fsp-st-rec fsp-num">
              {`${r.pointsFor.toFixed(1)} of ${r.maxPoints.toFixed(1)}`}
            </span>
          </TeamLine>
        ))}
      </div>
    </Panel>
  );
}

/**
 * THE ALL-PLAY RECORD (B-387, DETAIL_LEVELS §Standings at Everything): each
 * team's record had it played every team every week, beside the record it
 * has. The schedule's luck is the difference, and the numbers say it.
 */
export function AllPlay({ view }: { view: StandingsView }) {
  const share = (r: StandingsRow) => {
    const a = r.allPlay;
    if (a === null) return -1;
    const games = a.wins + a.losses + a.ties;
    return games === 0 ? -1 : (a.wins + a.ties / 2) / games;
  };
  const rows = [...view.rows]
    .filter((r) => r.allPlay !== null)
    .sort((a, b) => share(b) - share(a) || b.pointsFor - a.pointsFor);
  return (
    <Panel
      title="All-play"
      meta={
        <span className="fsp-muted fsp-sb-count">
          <Term tip={GLOSSARY.allPlayRecord}>What this is</Term>
        </span>
      }
    >
      <div className="fsp-st-list" aria-label="Every team, the best all-play record first">
        <div className="fsp-st-list-head" aria-hidden="true">
          <span />
          <span />
          <span />
          <span>All-play</span>
          <span>W–L</span>
        </div>
        {rows.map((r) => (
          <TeamLine
            key={r.team.id}
            row={r}
            place={1 + rows.filter((o) => share(o) > share(r)).length}
          >
            <span className="fsp-st-name">{r.team.name}</span>
            <span className="fsp-st-rec fsp-num">
              {recordText(r.allPlay!)}
              <span className="fsp-muted"> {shareText(r.allPlay!)}</span>
            </span>
            <span className="fsp-st-extra fsp-num fsp-muted">{recordText(r)}</span>
          </TeamLine>
        ))}
      </div>
    </Panel>
  );
}
